/**
 * File: useLeadsMutations.ts
 * Path: apps/admin/src/shared/hooks/useLeadsMutations.ts
 * Description: Mutation hooks split out of useLeads.ts.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type Consent,
  type Lead,
  type LeadContactType,
  type LeadDetails,
  type LeadOrigin,
  type LeadStage,
  type MapLeadCandidate,
} from "@idk/config";
import { toast } from "@idk/ui";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { clientsKey } from "@/shared/hooks/useClientsQueries";
import { documentsRepo } from "@/shared/services/documentsService";
import { leadsRepo, type Actor } from "../services/leadsService";
import { activityKey, auditsKey, detailKey, listKey } from "./useLeadsQueries";

/** What a Maps search was for; saved leads are tagged with it. */
export interface MapSearch {
  keyword: string;
  location: string;
}
const reportError = (action: string) => (error: unknown) => {
  console.error(`[Leads] ${action} failed:`, error);
  toast.error(error instanceof Error ? error.message : `Could not ${action}.`);
};
/** The signed-in admin, for history entries. */
export const useActor = (): Actor => {
  const { user } = useAuth();
  return { uid: user?.uid ?? "", email: user?.email ?? "" };
};
/** One Maps search. A mutation, not a query: each run is a billed request. */
export const useMapSearch = () =>
  useMutation({ mutationFn: leadsRepo.findOnMaps });
export const useLeadMutations = () => {
  const queryClient = useQueryClient();
  const actor = useActor();
  const refresh = (id?: string) => () => {
    void queryClient.invalidateQueries({ queryKey: listKey });
    if (id) {
      void queryClient.invalidateQueries({ queryKey: detailKey(id) });
      void queryClient.invalidateQueries({ queryKey: activityKey(id) });
      void queryClient.invalidateQueries({ queryKey: auditsKey(id) });
    }
  };

  return {
    /**
     * Marking leads lost, the one way it is done everywhere: they move to
     * Lost with the reason, and what to do better next time goes in each
     * one's history. Leads are never deleted from here.
     */
    markLost: useMutation({
      mutationFn: async (input: { leads: readonly Lead[]; reason: string; lesson: string }) => {
        for (const lead of input.leads) {
          await leadsRepo.moveStage(lead, "lost", actor, input.reason, input.lesson);
        }
      },
      // Settled, not success: a failure part-way still leaves earlier moves done.
      onSettled: (_data, _error, input) => {
        refresh()();
        input.leads.forEach((lead) => refresh(lead.id)());
      },
      onSuccess: (_data, input) => {
        const what = input.leads.length === 1 ? input.leads[0].name : `${input.leads.length} leads`;
        toast.success(`${what} marked as lost. The reason is in the history.`);
      },
      onError: reportError("mark the lead as lost"),
    }),

    /** Where a lead came from, changed after the fact. */
    setOrigin: useMutation({
      mutationFn: (input: { leadId: string; origin: LeadOrigin }) =>
        leadsRepo.setOrigin(input.leadId, input.origin, actor),
      onSuccess: (_data, input) => refresh(input.leadId)(),
      onError: reportError("save the source"),
    }),

    /** A call, WhatsApp, email or meeting that happened, and what came of it. */
    logContact: useMutation({
      mutationFn: (input: { leadId: string; type: LeadContactType; text: string }) =>
        leadsRepo.logContact(input.leadId, input.type, input.text, actor),
      onSuccess: (_data, input) => refresh(input.leadId)(),
      onError: reportError("log the contact"),
    }),

    /** A report from an audit run elsewhere, kept on the lead, which now counts as audited. */
    attachAuditReport: useMutation({
      mutationFn: async (input: { leadId: string; file: File }) => {
        await documentsRepo.upload({ kind: "leads", id: input.leadId }, input.file, "audit");
        await leadsRepo.markAuditUploaded(input.leadId, input.file.name, actor);
      },
      onSuccess: (_data, input) => {
        refresh(input.leadId)();
        void queryClient.invalidateQueries({ queryKey: ["documents"] });
        toast.success(`Attached ${input.file.name}. The lead is marked as audited.`);
      },
      onError: reportError("attach the report"),
    }),

    saveFromMaps: useMutation({
      mutationFn: (input: { candidates: MapLeadCandidate[]; search: MapSearch }) =>
        leadsRepo.saveFromMaps(input.candidates, input.search, actor),
      onSuccess: refresh(),
      onError: reportError("save the leads"),
    }),

    create: useMutation({
      mutationFn: (input: { details: LeadDetails; origin: LeadOrigin }) =>
        leadsRepo.create(input.details, input.origin, actor),
      onSuccess: refresh(),
      onError: reportError("add the lead"),
    }),

    update: useMutation({
      mutationFn: (input: { id: string; details: Partial<LeadDetails> }) =>
        leadsRepo.update(input.id, input.details),
      onSuccess: (_data, input) => refresh(input.id)(),
      onError: reportError("save the lead"),
    }),

    /**
     * Moves the card at once and puts it back if the write fails, so the
     * board never shows a move that didn't happen.
     */
    moveStage: useMutation({
      mutationFn: (input: { lead: Lead; to: LeadStage; lostReason?: string; lesson?: string }) =>
        leadsRepo.moveStage(input.lead, input.to, actor, input.lostReason, input.lesson),
      onMutate: async ({ lead, to }) => {
        await queryClient.cancelQueries({ queryKey: listKey });
        const previous = queryClient.getQueryData<Lead[]>(listKey);
        queryClient.setQueryData<Lead[]>(listKey, (rows) =>
          rows?.map((row) => (row.id === lead.id ? { ...row, stage: to } : row)),
        );
        return { previous };
      },
      onError: (error, _input, context) => {
        if (context?.previous) queryClient.setQueryData(listKey, context.previous);
        reportError("move the lead")(error);
      },
      onSettled: (_data, _error, input) => {
        refresh(input.lead.id)();
        // A lead moved to Won is now also a client.
        if (input.to === "won") void queryClient.invalidateQueries({ queryKey: clientsKey });
      },
    }),

    sendOpener: useMutation({
      mutationFn: (input: { lead: Lead; text: string; followUp: string }) =>
        leadsRepo.recordOpener(input.lead, input.text, input.followUp, actor),
      onSuccess: (_data, input) => refresh(input.lead.id)(),
      onError: reportError("record the message"),
    }),

    /** One Gemini call per click; nothing is saved until it is sent. */
    draftAuditEmail: useMutation({
      mutationFn: (leadId: string) => leadsRepo.draftAuditEmail(leadId),
    }),

    sendAuditEmail: useMutation({
      mutationFn: (input: { lead: Lead; subject: string; body: string; followUp: string }) =>
        leadsRepo.recordAuditEmail(input.lead, { subject: input.subject, body: input.body }, input.followUp, actor),
      onSuccess: (_data, input) => refresh(input.lead.id)(),
      onError: reportError("record the email"),
    }),

    logUnansweredCall: useMutation({
      mutationFn: (input: { lead: Lead; today: string }) =>
        leadsRepo.logUnansweredCall(input.lead, input.today, actor),
      onSuccess: (_data, input) => refresh(input.lead.id)(),
      onError: reportError("log the call"),
    }),

    /** What to do next on a lead, and when. */
    setNextAction: useMutation({
      mutationFn: (input: { leadId: string; type: LeadContactType; due: string }) =>
        leadsRepo.setNextAction(input.leadId, { type: input.type, due: input.due }, actor),
      onSuccess: (_data, input) => refresh(input.leadId)(),
      onError: reportError("save the next step"),
    }),

    /** Keeps the next step but clears its date, to be set once they confirm. */
    setNextStepTbd: useMutation({
      mutationFn: (input: { leadId: string; type: LeadContactType }) =>
        leadsRepo.setNextStepTbd(input.leadId, input.type, actor),
      onSuccess: (_data, input) => refresh(input.leadId)(),
      onError: reportError("save the next step"),
    }),

    addNote: useMutation({
      mutationFn: (input: { leadId: string; text: string }) =>
        leadsRepo.addNote(input.leadId, input.text, actor),
      onSuccess: (_data, input) => refresh(input.leadId)(),
      onError: reportError("add the note"),
    }),

    setParked: useMutation({
      mutationFn: (input: { leads: readonly Lead[]; parked: boolean }) =>
        leadsRepo.setParked(input.leads, input.parked, actor),
      onSuccess: (_data, input) => {
        refresh()();
        input.leads.forEach((lead) => refresh(lead.id)());
      },
      onError: reportError("park the leads"),
    }),

    /** Several leads to one stage, one transaction each (moveStage). */
    moveMany: useMutation({
      mutationFn: async (input: { leads: readonly Lead[]; to: LeadStage; lostReason?: string; lesson?: string }) => {
        for (const lead of input.leads) {
          await leadsRepo.moveStage(lead, input.to, actor, input.lostReason, input.lesson);
        }
      },
      // Settled, not success: a failure part-way still leaves earlier moves done.
      onSettled: (_data, _error, input) => {
        refresh()();
        input.leads.forEach((lead) => refresh(lead.id)());
        if (input.to === "won") void queryClient.invalidateQueries({ queryKey: clientsKey });
      },
      onError: reportError("move the leads"),
    }),

    removeMany: useMutation({
      mutationFn: async (leadIds: readonly string[]) => {
        for (const id of leadIds) await leadsRepo.remove(id);
      },
      onSettled: refresh(),
      onError: reportError("delete the leads"),
    }),

    remove: useMutation({
      mutationFn: (leadId: string) => leadsRepo.remove(leadId),
      onSuccess: refresh(),
      onError: reportError("delete the lead"),
    }),

    /** Their answer to "can I send you...": yes, stop or an existing client. */
    setConsent: useMutation({
      mutationFn: (input: { leadId: string; consent: Consent }) =>
        leadsRepo.setConsent(input.leadId, input.consent, actor),
      onSuccess: (_data, input) => refresh(input.leadId)(),
      onError: reportError("save their answer"),
    }),

    /** "Remove this person's details": see leadsRepo.removeDetails. */
    removeDetails: useMutation({
      mutationFn: async (lead: Lead) => {
        // Their attached files are personal details too: delete them first, so
        // a failure stops the removal instead of leaving files with no record.
        await documentsRepo.removeAll({ kind: "leads", id: lead.id });
        await leadsRepo.removeDetails(lead);
      },
      onSuccess: (_data, lead) => {
        refresh(lead.id)();
        toast.success("Their details were removed.");
      },
      onError: reportError("remove their details"),
    }),

    /** Everything held about the lead, as an object to save as a file. */
    exportData: useMutation({
      mutationFn: (leadId: string) => leadsRepo.exportData(leadId),
      onError: reportError("export their data"),
    }),
  };
};
